import React, { useState } from 'react';
import { importsAPI } from '@/lib/api';
import { Download, Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const AdminImportPage = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const response = await importsAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_productos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Plantilla descargada');
    } catch (error) {
      toast.error('Error al descargar plantilla');
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.csv')) {
      toast.error('Solo se permiten archivos .xlsx o .csv');
      return;
    }

    setFile(selectedFile);
    setPreview(null);
    setResult(null);

    try {
      const response = await importsAPI.preview(selectedFile);
      setPreview(response.data);
    } catch (error) {
      toast.error('Error al validar archivo');
    }
  };

  const handleImport = async (mode = 'create') => {
    if (!file) return;

    setImporting(true);
    try {
      const response = await importsAPI.execute(file, mode);
      setResult(response.data);
      toast.success('Importación completada');
      setFile(null);
      setPreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-import-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Importar Productos</h1>
          <p className="text-muted-foreground mt-1">Carga masiva desde Excel</p>
        </div>
        <Button onClick={handleDownloadTemplate} variant="outline" data-testid="download-template-button">
          <Download className="w-4 h-4 mr-2" />
          Descargar Plantilla
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
        <h2 className="text-lg font-heading font-bold text-slate-900 mb-4">1. Descargar plantilla Excel</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Descarga la plantilla oficial, completa los datos de productos y guárdala.
        </p>
        <Button onClick={handleDownloadTemplate} variant="outline">
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Descargar plantilla_productos.xlsx
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
        <h2 className="text-lg font-heading font-bold text-slate-900 mb-4">2. Cargar archivo completado</h2>
        <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-primary transition-colors">
          <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-sm text-muted-foreground mb-4">
            Arrastra tu archivo o haz clic para seleccionar
          </p>
          <input
            type="file"
            accept=".xlsx,.csv"
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
          />
          <label htmlFor="file-upload">
            <Button type="button" variant="outline" onClick={() => document.getElementById('file-upload').click()}>
              Seleccionar archivo
            </Button>
          </label>
          {file && (
            <p className="text-sm text-slate-600 mt-4">
              Archivo seleccionado: <span className="font-semibold">{file.name}</span>
            </p>
          )}
        </div>
      </div>

      {preview && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
          <h2 className="text-lg font-heading font-bold text-slate-900 mb-4">3. Validación</h2>
          
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-600 mb-1">Total Filas</p>
              <p className="text-2xl font-bold text-blue-700">{preview.total_rows}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-600 mb-1">Filas Válidas</p>
              <p className="text-2xl font-bold text-green-700">{preview.valid_rows}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600 mb-1">Filas con Error</p>
              <p className="text-2xl font-bold text-red-700">{preview.invalid_rows}</p>
            </div>
          </div>

          {preview.invalid_rows > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                <XCircle className="w-5 h-5" />
                Errores Encontrados
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {preview.invalid_data.map((item) => (
                  <div key={`row-${item.row}`} className="text-sm">
                    <span className="font-semibold text-red-700">Fila {item.row}:</span>
                    <ul className="ml-4 mt-1">
                      {item.errors.map((err, i) => (
                        <li key={`row-${item.row}-err-${i}`} className="text-red-600">- {err}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {preview.invalid_rows > 0 && preview.valid_rows > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                Se encontraron {preview.invalid_rows} filas con errores. Puedes importar solo las {preview.valid_rows} filas válidas o corregir el archivo.
              </p>
            </div>
          )}

          {/* Botones de acción - siempre visibles si hay filas válidas */}
          {preview.valid_rows > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
              <h3 className="font-semibold text-slate-900 mb-4">4. Ejecutar Importación</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {preview.invalid_rows === 0 
                  ? `Se importarán ${preview.valid_rows} productos.`
                  : `Se importarán solo las ${preview.valid_rows} filas válidas (se omitirán las ${preview.invalid_rows} con errores).`
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => handleImport('create')}
                  disabled={importing}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  size="lg"
                  data-testid="import-create-button"
                >
                  {importing ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      Importando...
                    </span>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Crear Nuevos Productos
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleImport('update')}
                  disabled={importing}
                  variant="default"
                  size="lg"
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="import-update-button"
                >
                  <Upload className="w-5 h-5 mr-2" />
                  Actualizar Existentes
                </Button>
              </div>
              <div className="mt-4 p-3 bg-white rounded border border-slate-200">
                <p className="text-xs text-slate-600">
                  <strong className="text-green-700">Crear Nuevos:</strong> Solo agrega productos que no existen en el sistema (omite códigos duplicados).<br/>
                  <strong className="text-blue-700">Actualizar:</strong> Modifica los datos de productos existentes y también agrega los nuevos.
                </p>
              </div>
            </div>
          )}

          {preview.valid_rows === 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <XCircle className="w-4 h-4 inline mr-2" />
                No hay filas válidas para importar. Por favor corrige todos los errores en el archivo.
              </p>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
          <h2 className="text-lg font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-green-600" />
            Resultado de Importación
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-600 mb-1">Total Procesadas</p>
              <p className="text-2xl font-bold text-blue-700">{result.total_rows}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-600 mb-1">Creados</p>
              <p className="text-2xl font-bold text-green-700">{result.created}</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-600 mb-1">Actualizados</p>
              <p className="text-2xl font-bold text-purple-700">{result.updated}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-sm text-slate-600 mb-1">Omitidos</p>
              <p className="text-2xl font-bold text-slate-700">{result.skipped}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminImportPage;